import type { FC, FormEvent, ReactNode } from 'react'
import type { ClinicSettings, Patient, PrintLayout } from '@/lib/clinic/types'

import { useMemo, useState } from 'react'
import { toast } from 'sonner'
import { cn } from '@/lib/utils'
import { useClinic } from '@/hooks/use-clinic'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { SectionCard } from '@/components/app/common'
import { CheckRow, FormField, preventEnterSubmit } from '@/components/app/form-bits'
import { PrescriptionSheet } from '@/components/prescriptions/prescription-sheet'
import { ScaledSheet } from '@/components/prescriptions/scaled-sheet'
import { SaveBar } from '@/components/settings/clinic-settings'
import { addDays } from '@/lib/clinic/dates'

type PrescriptionForm = ClinicSettings['prescription']

const LAYOUTS: Array<{ value: PrintLayout, title: string, description: string }> = [
    { value: 'full', title: 'Full page', description: 'Prints the clinic header and footer on plain A4 paper.' },
    { value: 'letterhead', title: 'Pre-printed letterhead', description: 'Leaves space at the top for paper that already has your header.' }
]

const SAMPLE_PATIENT: Patient = {
    id: 'DSC-1001',
    name: 'Sample Patient',
    phone: '98470 12345',
    age: 34,
    ageRecordedOn: null,
    dateOfBirth: null,
    gender: 'female',
    place: 'Kakkanad',
    address: '',
    medicalHistory: '',
    allergies: 'Penicillin',
    notes: '',
    createdAt: new Date(0).toISOString(),
    updatedAt: new Date(0).toISOString()
}

export const PrescriptionSettingsPanel: FC = (): ReactNode => {
    const { data, index, today, updateSettings } = useClinic()
    const [form, setForm] = useState<PrescriptionForm>(data.settings.prescription)
    const dirty = JSON.stringify(form) !== JSON.stringify(data.settings.prescription)

    const set = <K extends keyof PrescriptionForm>(key: K, value: PrescriptionForm[K]): void => {
        setForm(previous => ({ ...previous, [key]: value }))
    }

    const sample = useMemo(() => {
        const latest = [...data.prescriptions].sort((a, b) => b.createdAt.localeCompare(a.createdAt))[0]
        const patient = latest ? index.patientsById.get(latest.patientId) : undefined

        if (latest && patient) return { prescription: latest, patient }

        return {
            patient: SAMPLE_PATIENT,
            prescription: {
                id: 'RX-1001',
                date: today,
                complaint: 'Tooth Pain',
                diagnosis: 'Irreversible pulpitis in 36',
                advice: 'Root canal treatment started.',
                medicines: [
                    { id: 'sample-1', name: 'Clindamycin 300 mg', dosage: '1 capsule', frequency: '1-1-1', duration: '5 days', instructions: 'After food' },
                    { id: 'sample-2', name: 'Paracetamol 650 mg', dosage: '1 tablet', frequency: '1-0-1', duration: '3 days', instructions: 'After food' }
                ],
                instructions: form.defaultInstructions,
                notes: '',
                printNotes: false
            }
        }
    }, [data.prescriptions, index, today, form.defaultInstructions])

    const submit = (event: FormEvent<HTMLFormElement>): void => {
        event.preventDefault()
        updateSettings(settings => ({ ...settings, prescription: form }))
        toast.success('Prescription settings saved')
    }

    const previewSettings: ClinicSettings = { ...data.settings, prescription: form }

    return (
        <form onSubmit={submit} onKeyDown={preventEnterSubmit} noValidate className='grid gap-6 xl:grid-cols-[minmax(0,1fr)_22rem]'>
            <div className='grid min-w-0 gap-6'>
                <SectionCard title='Print layout' description='Prescriptions always print on A4.'>
                    <div role='radiogroup' aria-label='Print layout' className='grid gap-3 sm:grid-cols-2'>
                        {LAYOUTS.map(layout => (
                            <button
                                key={layout.value}
                                type='button'
                                role='radio'
                                aria-checked={form.layout === layout.value}
                                onClick={() => set('layout', layout.value)}
                                className={cn(
                                    'rounded-xl border bg-white p-4 text-left transition-colors outline-none hover:bg-muted/40 focus-visible:ring-3 focus-visible:ring-ring/50',
                                    form.layout === layout.value && 'border-primary/40 bg-accent/60 ring-1 ring-primary/20'
                                )}
                            >
                                <span className='block text-sm font-semibold text-foreground'>{layout.title}</span>
                                <span className='mt-1 block text-xs leading-relaxed text-muted-foreground'>{layout.description}</span>
                            </button>
                        ))}
                    </div>

                    <div className='mt-4 grid gap-3 md:grid-cols-3'>
                        <CheckRow id='rx-show-logo' checked={form.showLogo} onCheckedChange={value => set('showLogo', value)} label='Show logo' description='In the header' />
                        <CheckRow id='rx-show-allergies' checked={form.showAllergies} onCheckedChange={value => set('showAllergies', value)} label='Show allergies' description='Highlighted for safety' />
                        <CheckRow id='rx-show-next' checked={form.showNextVisit} onCheckedChange={value => set('showNextVisit', value)} label='Show next visit' description='When a follow-up is set' />
                    </div>
                </SectionCard>

                <SectionCard title='Default content' description='Filled in on every new prescription. You can still edit it each time.'>
                    <div className='grid gap-5'>
                        <FormField label='Default instructions' htmlFor='rx-default-instructions' hint='One instruction per line.'>
                            <Textarea id='rx-default-instructions' value={form.defaultInstructions} onChange={event => set('defaultInstructions', event.target.value)} rows={4} className='leading-relaxed' />
                        </FormField>

                        <FormField label='Prescription footer' htmlFor='rx-footer' hint='Printed at the bottom of the page in the full page layout.'>
                            <Textarea id='rx-footer' value={form.footer} onChange={event => set('footer', event.target.value)} rows={2} />
                        </FormField>
                    </div>
                </SectionCard>

                <SectionCard title='Doctor signature' description='Printed under the signature line.'>
                    <div className='grid gap-5 sm:grid-cols-2'>
                        <FormField label='Name' htmlFor='rx-signature-name'>
                            <Input id='rx-signature-name' value={form.signatureName} onChange={event => set('signatureName', event.target.value)} placeholder={data.settings.clinic.doctorName} />
                        </FormField>

                        <FormField label='Title or qualification' htmlFor='rx-signature-title'>
                            <Input id='rx-signature-title' value={form.signatureTitle} onChange={event => set('signatureTitle', event.target.value)} placeholder='e.g. BDS, MDS' />
                        </FormField>
                    </div>
                </SectionCard>

                <SaveBar dirty={dirty} onReset={() => setForm(data.settings.prescription)} />
            </div>

            <aside className='grid content-start gap-3 xl:sticky xl:top-24'>
                <p className='text-sm font-semibold text-foreground'>
                    Preview
                </p>

                <div className='rounded-2xl bg-slate-100 p-3'>
                    <ScaledSheet>
                        <PrescriptionSheet
                            prescription={sample.prescription}
                            patient={sample.patient}
                            settings={previewSettings}
                            nextVisit={form.showNextVisit ? { date: addDays(today, 7), time: '10:30' } : null}
                            className='shadow-lg'
                        />
                    </ScaledSheet>
                </div>

                <p className='text-xs text-muted-foreground'>
                    Shows your latest prescription with the settings above, before you save.
                </p>
            </aside>
        </form>
    )
}
